/**
 * useGreetings Hook — Greeting 数据的获取与操作
 *
 * 封装对 /api/greetings 的 CRUD 操作，管理加载状态和错误。
 *
 * SSR 支持：
 * - 服务端渲染时 loader 预取数据，通过 globalThis.__SSR_config.loaderData 注入
 * - 客户端 hydration 时优先使用预取数据，避免重复请求
 */
import { useCallback, useEffect, useState } from 'react';
/** /api/greetings 基础 URL */
const API_BASE = '/api/greetings';
/**
 * 获取 SSR 预取的 greetings 数据
 */
function getPrefetchedGreetings() {
    if (typeof globalThis === 'undefined')
        return null;
    const config = globalThis.__SSR_config;
    if (!config?.enabled || !config.loaderData)
        return null;
    const data = config.loaderData['/greetings'];
    if (Array.isArray(data))
        return data;
    return null;
}
/**
 * useGreetings — Greeting 数据管理 Hook
 *
 * 自动在 mount 时加载列表，提供 createGreeting 方法。
 * 如果 SSR 注入了预取数据，hydration 时直接使用，无需再次请求。
 *
 * @param initialGreetings — SSR 预取的初始数据（由 GreetingList 通过 props 传递）
 */
export function useGreetings(initialGreetings) {
    // 优先使用 SSR 预取数据初始化状态（优先 props，其次全局配置）
    const prefetchedFromGlobal = getPrefetchedGreetings();
    const prefetched = initialGreetings ?? prefetchedFromGlobal;
    const [greetings, setGreetings] = useState(prefetched ?? []);
    const [loading, setLoading] = useState(prefetched ? false : true);
    const [error, setError] = useState(null);
    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(API_BASE);
            if (!res.ok) {
                const body = await res.json();
                throw new Error(body.error || `HTTP ${res.status}`);
            }
            const data = await res.json();
            setGreetings(data);
        }
        catch (err) {
            setError(err?.message ?? 'Unknown error');
        }
        finally {
            setLoading(false);
        }
    }, []);
    const createGreeting = useCallback(async (data) => {
        try {
            const res = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!res.ok) {
                const body = await res.json();
                throw new Error(body.error || `HTTP ${res.status}`);
            }
            const result = await res.json();
            // 创建成功后刷新列表
            await refresh();
            return result;
        }
        catch (err) {
            setError(err?.message ?? 'Unknown error');
            return null;
        }
    }, [refresh]);
    useEffect(() => {
        // 仅在没有 SSR 预取数据时才发起客户端请求
        if (!prefetched) {
            refresh();
        }
    }, [refresh, prefetched]);
    return { greetings, loading, error, refresh, createGreeting };
}
//# sourceMappingURL=use-greetings.js.map